import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import logger from '../utils/logger';
import { 
  Star, 
  ArrowLeft, 
  User, 
  Package, 
  ChevronLeft, 
  ChevronRight,
  SortAsc,
  Check,
  X
} from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const RatingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const driverId = searchParams.get('driverId');
  
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'
  const [headerBusinessName, setHeaderBusinessName] = useState('');

  // Redirect if no driver ID provided
  useEffect(() => {
    if (!driverId || driverId === 'undefined' || driverId === 'null') {
      navigate('/');
      return;
    }
  }, [driverId, navigate]);

  // Fetch ratings
  const fetchRatings = useCallback(async (page = 1) => {
    // Don't fetch if driverId is invalid
    if (!driverId || driverId === 'undefined' || driverId === 'null') {
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        driverId: driverId,
        sortOrder: sortOrder
      });

      const response = await axios.get(`${API_ENDPOINTS.RATINGS}/all?${params}`);
      
      if (response.data.success) {
        const data = response.data.data || [];

        // Set header business name if results exist
        if (data.length > 0) {
          const bn = data[0]?.driver?.businessName || '';
          setHeaderBusinessName(bn);
        } else {
          setHeaderBusinessName('');
        }

        setRatings(data);
        setPagination(response.data.pagination);
        setCurrentPage(page);
      } else {
        setError('Failed to fetch ratings');
      }
    } catch (error) {
      logger.error('Error fetching ratings:', error);
      setError('Error loading ratings');
      toast.error('שגיאה בטעינת הביקורות');
    } finally {
      setLoading(false);
    }
  }, [driverId, sortOrder]);

  useEffect(() => {
    if (driverId) {
      fetchRatings(1);
    }
  }, [driverId, fetchRatings]);

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchRatings(newPage);
  };

  // Handle sort order change
  const handleSortChange = (value) => {
    setSortOrder(value);
    // Re-fetch with new sort order
    fetchRatings(1);
  };

  // Render star rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`h-4 w-4 ${
          index < Math.round(rating) 
            ? 'text-yellow-400 fill-current' 
            : 'text-gray-300'
        }`}
      />
    ));
  };

  // Get status color
  const getStatusColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600 bg-green-50';
    if (rating >= 3.5) return 'text-yellow-600 bg-yellow-50';
    if (rating >= 2.5) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען ביקורות...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => fetchRatings(1)}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-hebrew">
      {/* Skip link targets */}
      <div id="main-content" className="sr-only" tabIndex="-1">תוכן ראשי</div>
      
      {/* Header */}
      <div id="navigation" className="bg-white shadow-sm border-b" tabIndex="-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {headerBusinessName ? `ביקורות ${headerBusinessName}` : 'ביקורות נהגים'}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Driver Summary Card */}
        {headerBusinessName && ratings.length > 0 && (
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 mb-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold mb-2">{headerBusinessName}</h2>
                <p className="text-orange-100 text-lg">ספק שירותי הובלה</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-1">
                  {ratings.length > 0 ? ratings[0].overallRating.toFixed(1) : '0.0'}
                </div>
                <div className="flex justify-center mb-2">
                  {renderStars(ratings.length > 0 ? ratings[0].overallRating : 0)}
                </div>
                <p className="text-orange-100 text-sm">
                  {pagination.totalRatings} ביקורות
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Controls: Sort */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 mb-2">מיון לפי ציון כולל</label>
              <div className="flex space-x-2 rtl:space-x-reverse">
                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="desc">מהגבוה לנמוך</option>
                  <option value="asc">מהנמוך לגבוה</option>
                </select>
                <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 flex items-center">
                  <SortAsc className="h-4 w-4" />
                </div>
              </div>
            </div>

            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                סה"כ {pagination.totalRatings} ביקורות
              </div>
            </div>
          </div>
        </div>

        {/* Ratings List */}
        <div className="space-y-6">
          {ratings.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">אין ביקורות זמינות</h3>
              <p className="text-gray-600">
                עדיין לא הוגשו ביקורות עבור נהג זה
              </p>
            </div>
          ) : (
            ratings.map((rating, index) => (
              <div key={rating._id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                {/* Rating Header */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 rtl:space-x-reverse">
                      <div className="bg-orange-100 p-3 rounded-full">
                        <User className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-base font-normal text-gray-900">
                          {rating.additionalComments}
                        </h3>
                        <p className="text-sm text-gray-500">
                          <span className="text-xs text-gray-400 mr-1">נכתב בתאריך:</span>
                          <span>{new Date(rating.createdAt).toLocaleDateString('he-IL')}</span>
                        </p>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-bold ${getStatusColor(rating.overallRating)}`}>
                        {rating.overallRating.toFixed(1)}
                        <Star className="h-5 w-5 mr-1 rtl:ml-1" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rating Details */}
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700 mb-2">אמינות</p>
                      <div className="flex justify-center mb-1">
                        {renderStars(rating.reliability)}
                      </div>
                      <p className="text-xs text-gray-500">{rating.reliability}/5</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700 mb-2">דיוק בזמנים</p>
                      <div className="flex justify-center mb-1">
                        {renderStars(rating.punctuality)}
                      </div>
                      <p className="text-xs text-gray-500">{rating.punctuality}/5</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700 mb-2">שלמות המטען</p>
                      <div className="flex justify-center mb-1">
                        {renderStars(rating.itemIntegrity)}
                      </div>
                      <p className="text-xs text-gray-500">{rating.itemIntegrity}/5</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700 mb-2">אדיבות</p>
                      <div className="flex justify-center mb-1">
                        {renderStars(rating.courtesy)}
                      </div>
                      <p className="text-xs text-gray-500">{rating.courtesy}/5</p>
                    </div>
                  </div>

                  {/* Boolean Ratings */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className={`flex items-center justify-center space-x-2 rtl:space-x-reverse p-3 rounded-lg ${rating.priceAccuracy ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {rating.priceAccuracy ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-red-600" />
                      )}
                      <span className="font-medium">מחיר הוגן</span>
                    </div>
                    <div className={`flex items-center justify-center space-x-2 rtl:space-x-reverse p-3 rounded-lg ${rating.wouldRecommend ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {rating.wouldRecommend ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-red-600" />
                      )}
                      <span className="font-medium">המלצה לחברים</span>
                    </div>
                    <div className={`flex items-center justify-center space-x-2 rtl:space-x-reverse p-3 rounded-lg ${rating.wouldUseAgain ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {rating.wouldUseAgain ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-red-600" />
                      )}
                      <span className="font-medium">שירות חוזר</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            
            <span className="px-4 py-2 text-sm text-gray-700">
              עמוד {currentPage} מתוך {pagination.totalPages}
            </span>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingPage;
